import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, Eye, Sparkles, MessageCircle, Info, Layers } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSiteSettings } from "@/context/SiteSettingsContext";
import { resolveImageUrl } from "@/utils/api";
import SetBreakdownModal from "./SetBreakdownModal";

export default function ProductCard({ product }) {
  const { addToCart } = useCart();
  const { settings } = useSiteSettings();
  const [selectedColor, setSelectedColor] = useState(
    product.available_colors && product.available_colors.length > 0
      ? product.available_colors[0]
      : "Standard Pack"
  );
  const [modalOpen, setModalOpen] = useState(false);

  const mainImage = resolveImageUrl(
    (product.images && product.images[0]) ||
      "https://images.unsplash.com/photo-1652722464455-ec026ef74703?crop=entropy&cs=srgb&fm=jpg&q=85"
  );

  const hasTiers = Array.isArray(product.pricing_tiers) && product.pricing_tiers.length > 0;
  const bestTier = hasTiers
    ? product.pricing_tiers.reduce((min, t) => (Number(t.price_per_set) < Number(min.price_per_set) ? t : min), product.pricing_tiers[0])
    : null;

  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1, selectedColor);
  };

  const handleWhatsAppOrder = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const phone = settings.whatsapp_number.replace(/[^0-9]/g, "");
    const msg = encodeURIComponent(
      `Assalam o Alaikum! I want to order this lace set:%0A*Product:* ${product.name}%0A*Category:* ${product.category}%0A*Color:* ${selectedColor}%0A*Retail Price:* Rs. ${product.retail_price.toLocaleString()}%0A*Set Type:* Complete Set Pack`
    );
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank");
  };

  return (
    <>
      <div 
        data-testid={`product-card-${product.id}`}
        className="group relative bg-white rounded-xl border border-[#E5E0D8] overflow-hidden flex flex-col transition-all duration-300 hover:border-[#D4AF37]/70 shadow-luxury hover:shadow-luxury-hover"
      >
        {/* Top Badges */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5 items-start">
          {product.is_complete_set && (
            <span 
              data-testid={`badge-complete-set-${product.id}`}
              className="px-2.5 py-1 bg-[#1C1917]/90 backdrop-blur-sm text-[#E6C687] text-[10px] uppercase font-bold tracking-wider rounded-md border border-[#D4AF37]/40 shadow-sm flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3 text-[#D4AF37]" />
              Complete Set Pack
            </span>
          )}
          {product.featured && (
            <span className="px-2 py-0.5 bg-[#B76E79] text-white text-[9px] uppercase font-bold tracking-wider rounded">
              Bestseller
            </span>
          )}
        </div>

        {/* Stock Status Badge */}
        <div className="absolute top-3 right-3 z-10">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded shadow-sm ${
            product.stock_status === "In Stock"
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-amber-50 text-amber-800 border border-amber-200"
          }`}>
            {product.stock_status || "In Stock"}
          </span>
        </div>

        {/* Product Image Container */}
        <Link to={`/products/${product.id}`} className="relative aspect-[4/3] sm:aspect-square overflow-hidden bg-[#FAF8F5] block">
          <img
            src={mainImage}
            alt={product.name}
            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />
        </Link>

        {/* Card Body */}
        <div className="p-4 flex-1 flex flex-col justify-between space-y-3">
          
          <div>
            {/* Category tag */}
            <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
              <span className="uppercase tracking-wider text-[10px] font-semibold text-[#996515]">
                {product.category}
              </span>
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                data-testid={`btn-view-set-breakdown-${product.id}`}
                className="text-stone-400 hover:text-[#996515] flex items-center gap-0.5 text-[10px]"
                title="View complete set contents"
              >
                <Info className="w-3 h-3" />
                <span>Set details</span>
              </button>
            </div>

            {/* Product Title */}
            <Link to={`/products/${product.id}`}>
              <h3 
                data-testid={`product-title-${product.id}`}
                className="font-heading text-base font-semibold text-stone-900 group-hover:text-[#996515] transition line-clamp-2 leading-snug"
              >
                {product.name}
              </h3>
            </Link>

            {/* Color Swatches */}
            {product.available_colors && product.available_colors.length > 0 && (
              <div className="mt-2.5 flex items-center gap-1.5 flex-wrap">
                <span className="text-[11px] text-stone-500 mr-1">Color:</span>
                {product.available_colors.slice(0, 4).map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setSelectedColor(color)}
                    data-testid={`color-swatch-${product.id}-${color.replace(/\s+/g, '-').toLowerCase()}`}
                    className={`text-[10px] px-2 py-0.5 rounded border transition ${
                      selectedColor === color
                        ? "bg-[#1C1917] text-white border-[#1C1917] font-medium"
                        : "bg-[#F5F2EB] text-stone-700 border-stone-200 hover:border-stone-400"
                    }`}
                  >
                    {color}
                  </button>
                ))}
                {product.available_colors.length > 4 && (
                  <span className="text-[10px] text-stone-400">+{product.available_colors.length - 4}</span>
                )}
              </div>
            )}
          </div>

          {/* Pricing Section */}
          <div className="pt-2 border-t border-[#E5E0D8]/60">
            <div className="flex items-baseline justify-between">
              <div>
                <span className="text-xs text-stone-400 block font-normal">Retail Price (Set)</span>
                <span 
                  data-testid={`product-retail-price-${product.id}`}
                  className="text-lg font-bold text-stone-900 tracking-tight"
                >
                  Rs. {product.retail_price ? product.retail_price.toLocaleString() : "0"}
                </span>
              </div>
              
              {product.wholesale_price && (
                <div className="text-right">
                  <span className="text-[10px] text-[#996515] uppercase font-bold block">Wholesale</span>
                  <span 
                    data-testid={`product-wholesale-price-${product.id}`}
                    className="text-xs font-semibold text-[#996515]"
                  >
                    Rs. {product.wholesale_price.toLocaleString()}
                  </span>
                  {bestTier && (
                    <span
                      data-testid={`product-best-tier-${product.id}`}
                      className="text-[9px] block text-emerald-700 font-semibold"
                    >
                      as low as Rs. {Number(bestTier.price_per_set).toLocaleString()} @ {bestTier.min_quantity}+ sets
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-2 mt-3.5">
              <button
                type="button"
                onClick={handleQuickAdd}
                data-testid={`btn-add-to-cart-${product.id}`}
                className="w-full py-2 px-2 bg-[#1C1917] hover:bg-[#D4AF37] text-white hover:text-[#1C1917] text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 transition duration-200 shadow-sm"
              >
                <ShoppingBag className="w-3.5 h-3.5" />
                <span>Add to Cart</span>
              </button>

              <button
                type="button"
                onClick={handleWhatsAppOrder}
                data-testid={`btn-whatsapp-order-${product.id}`}
                className="w-full py-2 px-2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-1 transition duration-200 shadow-sm"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                <span>WhatsApp</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Set Breakdown Modal */}
      {modalOpen && (
        <SetBreakdownModal 
          product={product} 
          onClose={() => setModalOpen(false)} 
        />
      )}
    </>
  );
}
